import { __extends } from "tslib";
import { isString } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import getTheme from '../../services/theme';
import getStyle from '../../services/get-style';
import Wix from '../../../services/wix-sdk-polyfill';
import { MAX_PINTEREST_IMAGE_SIZE } from '../../services/viewer';
import styles from './rich-content-viewer.scss';
import { viewerCustomStyleFn, styleSelectionPredicate, getTextColorSchema } from '../../services/text-color-utils';
import { createHashtagPath, createHashtagHref } from '../../services/search-page-url';
import linkSettings from '../../constants/linkSettings';
var RicosViewerWrapper = /** @class */ (function (_super) {
    __extends(RicosViewerWrapper, _super);
    function RicosViewerWrapper() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            error: false,
        };
        _this.getImagePluginConfig = function () {
            return {
                imageProps: function (src) { return ({
                    'data-pin-url': _this.props.pageUrl,
                    'data-pin-media': isString(src)
                        ? src
                        : _this.props.getImageUrl(src, MAX_PINTEREST_IMAGE_SIZE, MAX_PINTEREST_IMAGE_SIZE),
                }); },
            };
        };
        _this.getGalleryPluginConfig = function () {
            var _a = _this.props, isOOI = _a.isOOI, isInEditor = _a.isInEditor, isInPreview = _a.isInPreview;
            var useCustomScroll = !isOOI || (isOOI && (isInEditor() || isInPreview()));
            var galleryScrollingElement = function () { return ({
                addEventListener: function (eventName, callback) {
                    Wix.addEventListener(eventName.toUpperCase(), callback);
                },
                removeEventListener: function (eventName, callback) {
                    Wix.removeEventListener(eventName.toUpperCase(), callback);
                },
            }); };
            return {
                imageProps: function () { return ({ 'data-pin-url': _this.props.pageUrl }); },
                scrollingElement: useCustomScroll ? galleryScrollingElement : typeof window !== 'undefined' && window,
            };
        };
        _this.getTextColorPluginConfig = function () {
            var _a = _this.props, appSettings = _a.appSettings, isHexColor = _a.isHexColor;
            var colorScheme = getTextColorSchema(appSettings);
            return {
                customStyleFn: viewerCustomStyleFn(colorScheme, { isHexColor: isHexColor }),
                styleSelectionPredicate: styleSelectionPredicate(colorScheme, { isHexColor: isHexColor }),
            };
        };
        _this.getHashtagPluginConfig = function () {
            var _a = _this.props, hashtagNavigate = _a.hashtagNavigate, sectionUrl = _a.sectionUrl;
            return {
                onClick: function (event) {
                    event.preventDefault();
                    event.stopPropagation();
                    hashtagNavigate(createHashtagPath(event.target.innerText));
                },
                createHref: function (decoratedText) { return createHashtagHref(decoratedText, sectionUrl); },
            };
        };
        _this.getActionButtonPluginConfig = function () { return ({ onClick: _this.props.onButtonClick }); };
        _this.getPlugins = function () {
            var plugins = [
                _this.props.pluginCodeBlock(),
                _this.props.pluginDivider(),
                _this.props.pluginGallery(_this.getGalleryPluginConfig()),
                _this.props.pluginGiphy(),
                _this.props.pluginHashtag(_this.getHashtagPluginConfig()),
                _this.props.pluginHtml(),
                _this.props.pluginImage(_this.getImagePluginConfig()),
                _this.props.pluginLineSpacing(),
                _this.props.pluginLink(),
                _this.props.pluginTextColor(_this.getTextColorPluginConfig()),
                _this.props.pluginVideo(),
                _this.props.pluginFileUpload(),
                _this.props.pluginHeadersMarkdown(),
                _this.props.pluginActionButton(_this.getActionButtonPluginConfig()),
            ];
            return plugins;
        };
        _this.onError = function (error) {
            if (!_this.state.error) {
                _this.setState({ error: error });
            }
        };
        return _this;
    }
    RicosViewerWrapper.prototype.render = function () {
        if (this.state.error) {
            return this.props.fallback();
        }
        var _a = this.props, WixRicosViewer = _a.WixRicosViewer, content = _a.content, rceVersion = _a.rceVersion, _getTheme = _a.getTheme, _getStyle = _a.getStyle, isCompact = _a.isCompact, appSettings = _a.appSettings, isSeo = _a.isSeo, isOOI = _a.isOOI, isMobile = _a.isMobile, locale = _a.locale, richContentFontClassName = _a.richContentFontClassName, useDescriptionFontInEditor = _a.useDescriptionFontInEditor, isPostPageBundle = _a.isPostPageBundle, instance = _a.instance, css = _a.css, disableTheme = _a.disableTheme;
        var isPostPageEnabled = true;
        var readOnly = true;
        var isViewer = true;
        var isPostPage = isPostPageBundle();
        var plugins = this.getPlugins();
        var cssOverride = disableTheme
            ? {}
            : _getTheme({
                appSettings: appSettings,
                readOnly: readOnly,
                isCompact: isCompact,
                isViewer: isViewer,
                isSeo: isSeo,
                isOOI: isOOI,
                isMobile: isMobile,
                isPostPageEnabled: isPostPageEnabled,
                richContentFontClassName: richContentFontClassName,
                useDescriptionFontInEditor: useDescriptionFontInEditor,
                isPostPage: isPostPage,
                css: css,
                version: rceVersion,
            });
        var style = _getStyle(appSettings, { isPostPageEnabled: isPostPageEnabled });
        var className = classNames(styles.container, cssOverride.overrideDefaultTheme, cssOverride.overrideAppSettings);
        return (React.createElement("div", { style: style, className: className, "data-rce-version": rceVersion },
            React.createElement(WixRicosViewer, { plugins: plugins, content: content, cssOverride: cssOverride, locale: locale, instance: instance, onError: this.onError, isMobile: isMobile, seoSettings: isSeo, linkSettings: linkSettings, biSettings: { consumer: 'Blog' } })));
    };
    return RicosViewerWrapper;
}(React.Component));
RicosViewerWrapper.propTypes = {
    WixRicosViewer: PropTypes.elementType.isRequired,
    pluginCodeBlock: PropTypes.func.isRequired,
    pluginDivider: PropTypes.func.isRequired,
    pluginGallery: PropTypes.func.isRequired,
    pluginGiphy: PropTypes.func.isRequired,
    pluginHashtag: PropTypes.func.isRequired,
    pluginHtml: PropTypes.func.isRequired,
    pluginImage: PropTypes.func.isRequired,
    pluginLineSpacing: PropTypes.func.isRequired,
    pluginLink: PropTypes.func.isRequired,
    pluginTextColor: PropTypes.func.isRequired,
    pluginVideo: PropTypes.func.isRequired,
    pluginFileUpload: PropTypes.func.isRequired,
    pluginHeadersMarkdown: PropTypes.func.isRequired,
    pluginActionButton: PropTypes.func.isRequired,
    content: PropTypes.object.isRequired,
    rceVersion: PropTypes.string.isRequired,
    getTheme: PropTypes.func.isRequired,
    getStyle: PropTypes.func.isRequired,
    isCompact: PropTypes.bool.isRequired,
    appSettings: PropTypes.object.isRequired,
    isSeo: PropTypes.bool.isRequired,
    isInEditor: PropTypes.func.isRequired,
    isInPreview: PropTypes.func.isRequired,
    isOOI: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    richContentFontClassName: PropTypes.string.isRequired,
    useDescriptionFontInEditor: PropTypes.string.isRequired,
    isPostPageBundle: PropTypes.bool.isRequired,
    instance: PropTypes.string.isRequired,
    getImageUrl: PropTypes.func.isRequired,
    pageUrl: PropTypes.string.isRequired,
    isHexColor: PropTypes.func.isRequired,
    hashtagNavigate: PropTypes.func.isRequired,
    sectionUrl: PropTypes.string.isRequired,
    onButtonClick: PropTypes.func,
    css: PropTypes.func,
    disableTheme: PropTypes.bool,
};
RicosViewerWrapper.defaultProps = {
    isCompact: false,
    useDescriptionFontInEditor: true,
    fallback: function () { return 'Content cannot be displayed'; },
    getTheme: getTheme,
    getStyle: getStyle,
};
export default RicosViewerWrapper;
